import { supabase } from './supabase';
import { globalState, syncActions, userActions, UserProfile, companyActions, CompanyProfile } from '../state/globalState';
import { RealtimeLogger } from '../utils/logger';
import { Platform } from 'react-native';
import { databaseService } from './databaseService';
import { APP_CONSTANTS } from '../config/constants';
import { checkSubscriptionStatus } from './subscriptionService';
import { realtimeTrigger } from '../state/realtime';
import * as FileSystem from 'expo-file-system/legacy';
import { downloadRemoteImageToFile } from '@/utils/file/downloadRemoteImageIOS';
import { sanitizeLocalTablePayload } from '../utils/localTableSchema';
import {
  captureSentryExceptionWithScope,
  captureSentryMessageWithScope,
} from './monitoring/sentry';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import { isRecoveryRuntimeBlocked } from './recovery/RecoveryRuntimeGate';
import { markChecklistSnapshotPending } from './checklist/ChecklistSnapshotVisibilityService';
import { triggerSyncSoon } from './syncScheduler';
import type { HistoricalInsertGuard } from './sync/HistoricalInsertGuard';

// Armazenar referências às subscrições para fácil limpeza
interface SubscriptionRef {
  id: string;
  table: string;
  callback?: () => void;
  cancelReady?: () => void;
  isReady?: () => boolean;
  unsubscribe: () => void;
}

// Array de referências de todas as subscrições ativas
const subscriptions: SubscriptionRef[] = [];
const REALTIME_SUBSCRIPTION_READY_TIMEOUT_MS = 15000;
let subscriptionGeneration = 0;

// O Supabase entrega INSERT e UPDATE por canais distintos e os callbacks
// podem se sobrepor. A fila cobre apenas ler -> comparar -> aplicar no banco
// local; efeitos externos usam filas por recurso para não bloquear eventos de
// outras imagens durante um download.
let realtimeDatabaseQueue: Promise<void> = Promise.resolve();

// Um efeito pós-aplicação pode fazer I/O demorado depois que a mutação local
// terminou. Serializamos somente o mesmo registro (e não a fila global do
// banco), evitando que um UPDATE atrasado recrie um arquivo depois de um
// DELETE subsequente e mantendo efeitos de estado na mesma ordem do banco.
const realtimePostApplyQueues = new Map<string, Promise<void>>();

const enqueueRealtimePostApplyTask = (
  key: string | undefined,
  task: () => Promise<void>
): Promise<void> => {
  if (!key) return task();

  const previous = realtimePostApplyQueues.get(key) ?? Promise.resolve();
  const run = previous.then(task);
  const settled = run.then(() => undefined, () => undefined);
  realtimePostApplyQueues.set(key, settled);

  void settled.then(() => {
    if (realtimePostApplyQueues.get(key) === settled) {
      realtimePostApplyQueues.delete(key);
    }
  });

  return run;
};

const getRealtimePostApplyKey = (tableName: string, rawData: any): string | undefined => {
  const id = rawData?.id;
  return id === null || id === undefined ? undefined : `${tableName}:${String(id)}`;
};

const isRealtimeGenerationCurrent = (generation: number): boolean =>
  generation === subscriptionGeneration;

const enqueueRealtimeDatabaseTask = <T>(
  task: () => Promise<T>,
  label: string
): Promise<T> => {
  const run = realtimeDatabaseQueue.then(task, task);
  realtimeDatabaseQueue = run.then(() => undefined, () => undefined);
  RealtimeLogger.Events.debug(`[TX-GUARD] Evento realtime enfileirado: ${label}`);
  return run;
};

const areAllSubscriptionsReady = (): boolean =>
  subscriptions.length > 0 && subscriptions.every(subscription => subscription.isReady?.() === true);

const isRealtimeSetupIdentityCurrent = async (
  userId: string,
  expectedCompanyId?: string
): Promise<boolean> => {
  if (globalState.user.user.get()?.id !== userId) return false;
  if (!expectedCompanyId) return true;

  const profile = globalState.user.profile.get();
  if (profile?.id === userId && profile.empresa_id) {
    return profile.empresa_id === expectedCompanyId;
  }

  try {
    const localUser = await databaseService.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
      userId
    );
    return globalState.user.user.get()?.id === userId && localUser?.empresa_id === expectedCompanyId;
  } catch (error) {
    RealtimeLogger.Setup.warn('Não foi possível validar a empresa local antes do setup realtime:', error);
    return false;
  }
};

// Tabelas que não devem ser monitoradas por realtime
const EXCLUDE_TABLES = ['items', 'pending_operations', 'pending_images', 'pending_deletions'];

// Agrupador simples para consolidar logs de subscrição
const subscriptionLogAccumulator: {
  success: Set<string>;
  error: Set<string>;
  errorDetails: Map<string, string>;
  timeoutHandle: ReturnType<typeof setTimeout> | null;
} = {
  success: new Set(),
  error: new Set(),
  errorDetails: new Map(),
  timeoutHandle: null
};

const missingConfigAccumulator: {
  tables: Set<string>;
  timeoutHandle: ReturnType<typeof setTimeout> | null;
} = {
  tables: new Set(),
  timeoutHandle: null
};

const getRealtimeMonitoringContext = (): Record<string, unknown> => ({
  userId: globalState.user.user.get()?.id || null,
  empresaId:
    globalState.user.profile.get()?.empresa_id ||
    globalState.company.data.get()?.id ||
    null,
  networkConnected: globalState.network.isConnected.get(),
  isAuthenticated: globalState.user.isAuthenticated.get(),
});

const serializeRealtimeError = (error: unknown): string => {
  if (!error) {
    return 'indisponivel';
  }

  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  try {
    const serialized = JSON.stringify(error);
    if (serialized) {
      return serialized;
    }
  } catch {
    // Ignora erro de serialização para usar fallback abaixo
  }

  return String(error);
};

const normalizeRealtimeDedupePart = (value: string, maxLength: number = 220): string => {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'empty';
  }
  return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
};

const buildRealtimeSummaryDedupeKey = (
  errorEntries: string[],
  errorDetailMap: Record<string, string>
): string => {
  const sortedEntries = [...errorEntries].sort();
  const detailsSignature = sortedEntries
    .map(entry => `${entry}:${normalizeRealtimeDedupePart(errorDetailMap[entry] || 'sem_detalhe')}`)
    .join('|');

  const signature = normalizeRealtimeDedupePart(
    `${sortedEntries.join('|')}::${detailsSignature || 'sem_detalhes'}`,
    800
  );

  return `realtime:subscription_summary_error:${signature}`;
};

const flushSubscriptionStatusLog = () => {
  const successEntries = Array.from(subscriptionLogAccumulator.success);
  const errorEntries = Array.from(subscriptionLogAccumulator.error);

  if (!successEntries.length && !errorEntries.length) {
    return;
  }

  const successMessage = successEntries.length ? successEntries.join(', ') : 'nenhum';
  const errorMessage = errorEntries.length ? errorEntries.join(', ') : 'nenhum';

  if (errorEntries.length) {
    const subscriptionSummaryErrorMessage =
      `Resumo de subscrições realtime com erro - sucesso: ${successMessage}; erros: ${errorMessage}`;
    RealtimeLogger.Error.warn(subscriptionSummaryErrorMessage);
    const errorDetailMap = errorEntries.reduce<Record<string, string>>((acc, entry) => {
      const detail = subscriptionLogAccumulator.errorDetails.get(entry);
      if (detail) {
        acc[entry] = detail;
      }
      return acc;
    }, {});

    captureSentryMessageWithScope(subscriptionSummaryErrorMessage, {
      level: 'error',
      context: {
        ...getRealtimeMonitoringContext(),
        loggerMessage: subscriptionSummaryErrorMessage,
        successEntries,
        errorEntries,
        errorDetailMap,
      },
      tags: {
        flow: 'realtime',
        stage: 'subscription_setup',
        status: 'channel_error',
      },
      fingerprint: ['realtime', 'subscription_summary_error'],
      dedupeKey: buildRealtimeSummaryDedupeKey(errorEntries, errorDetailMap),
      dedupeWindowMs: 60000,
    });
  } else {
    RealtimeLogger.Setup.info(`Resumo de subscrições realtime - sucesso: ${successMessage}; erros: ${errorMessage}`);
  }

  subscriptionLogAccumulator.success.clear();
  subscriptionLogAccumulator.error.clear();
  subscriptionLogAccumulator.errorDetails.clear();
};

const scheduleSubscriptionStatusLog = () => {
  if (subscriptionLogAccumulator.timeoutHandle) {
    clearTimeout(subscriptionLogAccumulator.timeoutHandle);
  }

  subscriptionLogAccumulator.timeoutHandle = setTimeout(() => {
    subscriptionLogAccumulator.timeoutHandle = null;
    flushSubscriptionStatusLog();
  }, 100);
};

const registerSubscriptionStatus = (
  status: string,
  tableName: string,
  event: string,
  errorDetail?: string
) => {
  const channelId = `${tableName}:${event}`;

  if (status === 'SUBSCRIBED') {
    subscriptionLogAccumulator.success.add(channelId);
    scheduleSubscriptionStatusLog();
    return;
  }

  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
    subscriptionLogAccumulator.error.add(channelId);
    if (errorDetail) {
      subscriptionLogAccumulator.errorDetails.set(channelId, errorDetail);
    }
    scheduleSubscriptionStatusLog();
  }
};

const flushMissingConfigLog = () => {
  if (!missingConfigAccumulator.tables.size) {
    return;
  }

  const tables = Array.from(missingConfigAccumulator.tables).join(', ');
  RealtimeLogger.Setup.warn(`Tabelas sem configuração de sincronização ativa: ${tables}`);
  missingConfigAccumulator.tables.clear();
};

const scheduleMissingConfigLog = () => {
  if (missingConfigAccumulator.timeoutHandle) {
    clearTimeout(missingConfigAccumulator.timeoutHandle);
  }

  missingConfigAccumulator.timeoutHandle = setTimeout(() => {
    missingConfigAccumulator.timeoutHandle = null;
    flushMissingConfigLog();
  }, 100);
};

const registerTableWithoutSyncConfig = (tableName: string) => {
  missingConfigAccumulator.tables.add(tableName);
  scheduleMissingConfigLog();
};

/**
 * Tipos de filtros suportados pelo Supabase Realtime
 */
export enum FilterOperator {
  EQ = 'eq',     // equal
  NEQ = 'neq',   // not equal
  GT = 'gt',     // greater than
  GTE = 'gte',   // greater than or equal
  LT = 'lt',     // less than
  LTE = 'lte',   // less than or equal
  IN = 'in',     // in array
}

/**
 * Interface que define uma regra de filtro para uma subscrição
 */
interface SubscriptionFilter {
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
}

/**
 * Obtém lista de tabelas para monitoramento em tempo real
 * Filtro automático baseado nas constantes de configuração
 * 
 * @returns Array com nomes das tabelas a serem monitoradas
 */
const getMonitorableTables = (): string[] => {
  const allTables = Object.keys(APP_CONSTANTS.DATABASE.TABLES);
  const monitorableTables = allTables.filter(tableName => {
    // Verifica se a tabela não deve ser excluída do monitoramento
    const tableNameValue = APP_CONSTANTS.DATABASE.TABLES[tableName as keyof typeof APP_CONSTANTS.DATABASE.TABLES].NAME;
    return !EXCLUDE_TABLES.includes(tableNameValue);
  });

  return monitorableTables.map(key =>
    APP_CONSTANTS.DATABASE.TABLES[key as keyof typeof APP_CONSTANTS.DATABASE.TABLES].NAME
  );
};

/**
 * Configura as subscrições de realtime para as tabelas relevantes
 * Deve ser chamado apenas quando o usuário estiver autenticado e existir no banco local
 * 
 * @param userId ID do usuário autenticado
 * @returns Booleano indicando se a configuração foi bem-sucedida
 */
export const setupRealtimeSubscriptions = async (
  userId: string,
  expectedCompanyId?: string
): Promise<boolean> => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    RealtimeLogger.Setup.warn('[RECOVERY] Realtime bloqueado pela quarentena');
    cleanupRealtimeSubscriptions();
    return false;
  }

  if (isRecoveryRuntimeBlocked()) {
    RealtimeLogger.Setup.warn('[RECOVERY] Realtime bloqueado pelo gate runtime');
    cleanupRealtimeSubscriptions();
    return false;
  }

  //RealtimeLogger.Setup.info(`Configurando subscrições realtime para o usuário: ${userId.substring(0, 8)}...`);

  // Verifica se o usuário está autenticado e existe no banco
  if (!globalState.user.isAuthenticated.get() || !globalState.user.existsInDatabase.get()) {
    RealtimeLogger.Setup.warn('Não é possível configurar subscrições: usuário não autenticado ou não existe no banco');
    return false;
  }

  if (!(await isRealtimeSetupIdentityCurrent(userId, expectedCompanyId))) {
    RealtimeLogger.Setup.warn('Setup realtime ignorado: identidade solicitada não é mais a atual');
    return false;
  }

  // A identidade foi validada; agora esta execução pode substituir os canais atuais.
  cleanupRealtimeSubscriptions();
  const setupGeneration = subscriptionGeneration;
  const ownsCurrentSetup = () => setupGeneration === subscriptionGeneration;

  try {
    // Inicialização do client realtime no Supabase
    //RealtimeLogger.Setup.info('Inicializando cliente realtime do Supabase');

    // Obtém as tabelas para monitoramento
    const tables = getMonitorableTables();
    RealtimeLogger.Setup.info(`Tabelas a serem monitoradas: ${tables.join(', ')}`);

    // Configura subscrições para cada tabela
    const readinessChecks: Promise<boolean>[] = [];
    for (const tableName of tables) {
      // Determina filtros específicos baseados na tabela
      const filters = await determineTableFilters(tableName, userId);
      if (!ownsCurrentSetup()) return false;
      if (!(await isRealtimeSetupIdentityCurrent(userId, expectedCompanyId))) {
        if (ownsCurrentSetup()) cleanupRealtimeSubscriptions();
        return false;
      }

      // Configura as subscrições para cada filtro
      for (const filter of filters) {
        readinessChecks.push(setupTableSubscription(filter.table, filter.event, filter.filter, setupGeneration));
      }
    }

    const readinessResults = await Promise.all(readinessChecks);
    if (!ownsCurrentSetup()) return false;
    if (!(await isRealtimeSetupIdentityCurrent(userId, expectedCompanyId))) {
      if (ownsCurrentSetup()) cleanupRealtimeSubscriptions();
      return false;
    }
    if (readinessResults.some(isReady => !isReady)) {
      RealtimeLogger.Setup.warn('Uma ou mais subscrições realtime não ficaram prontas dentro do prazo');
      if (ownsCurrentSetup()) cleanupRealtimeSubscriptions();
      return false;
    }
    if (!areAllSubscriptionsReady()) {
      RealtimeLogger.Setup.warn('Uma ou mais subscrições realtime perderam prontidão durante a barreira');
      cleanupRealtimeSubscriptions();
      return false;
    }

    // Atualiza o estado global com informações sobre as subscrições ativas
    const subscribedTables = subscriptions.map(sub => sub.table);
    const subscriptionIds = subscriptions.map(sub => sub.id);

    // Usa a syncAction para atualizar o estado
    syncActions.updateRealtimeSubscriptions(true, subscribedTables, subscriptionIds);

    //RealtimeLogger.Setup.info(`Subscrições realtime configuradas com sucesso: ${subscriptions.length} canais`);
    return true;
  } catch (error) {
    const setupRealtimeErrorMessage = 'Erro ao configurar subscrições realtime';
    RealtimeLogger.Error.warn(`${setupRealtimeErrorMessage}:`, error);
    captureSentryExceptionWithScope(error, {
      context: {
        ...getRealtimeMonitoringContext(),
        stage: 'setup_realtime_subscriptions',
        loggerMessage: setupRealtimeErrorMessage,
      },
      tags: {
        flow: 'realtime',
        stage: 'setup',
      },
      fingerprint: ['realtime', 'setup_exception'],
      dedupeKey: 'realtime:setup_exception',
      dedupeWindowMs: 30000,
    });
    if (ownsCurrentSetup()) cleanupRealtimeSubscriptions();
    return false;
  }
};

/**
 * Determina os filtros específicos para uma tabela
 * 
 * @param tableName Nome da tabela
 * @param userId ID do usuário autenticado
 * @returns Array de filtros para a tabela
 */
const determineTableFilters = async (tableName: string, userId: string): Promise<SubscriptionFilter[]> => {
  const filters: SubscriptionFilter[] = [];
  let empresaId: string | null = null;

  // Obtém a configuração de sincronização da tabela
  const tableConfig = getTableSyncConfig(tableName);

  // Se não há configuração ou está desabilitada, retorna vazio
  if (!tableConfig || !tableConfig.enabled) {
    registerTableWithoutSyncConfig(tableName);
    return [];
  }

  // Verificar se o app requer empresa antes de tentar obter o empresaId
  if (APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY || tableConfig.forceCompanyFilter) {
    //RealtimeLogger.Setup.info(`Tentando obter empresa_id do usuário para os filtros de ${tableName}`);
    try {
      const userProfile = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
      if (userProfile && userProfile.empresa_id) {
        empresaId = userProfile.empresa_id;
        //RealtimeLogger.Setup.info(`ID da empresa do usuário obtido: ${empresaId}`);
      }
    } catch (error) {
      RealtimeLogger.Setup.warn(`Não foi possível obter a empresa do usuário: ${error}`);
    }
  }

  // Adiciona filtros de acordo com a configuração SYNC_CONFIG
  // Para INSERT, UPDATE e DELETE separadamente

  // Filtros para INSERT
  const insertFilter = buildFilterForConfig(tableName, tableConfig, userId, empresaId);
  if (insertFilter) {
    filters.push({
      table: tableName,
      event: 'INSERT',
      filter: insertFilter
    });
  } else {
    filters.push({
      table: tableName,
      event: 'INSERT'
    });
  }

  // Filtros para UPDATE
  const updateFilter = buildFilterForConfig(tableName, tableConfig, userId, empresaId);
  if (updateFilter) {
    filters.push({
      table: tableName,
      event: 'UPDATE',
      filter: updateFilter
    });
  } else {
    filters.push({
      table: tableName,
      event: 'UPDATE'
    });
  }

  // DELETE normalmente não suporta filtros no Supabase Realtime
  filters.push({
    table: tableName,
    event: 'DELETE'
  });

  // Se não conseguimos criar filtros específicos, adicione um aviso no log
  if (filters.length === 0) {
    RealtimeLogger.Setup.warn(`Não foi possível criar filtros para ${tableName}, usando configuração padrão`);
    filters.push(
      { table: tableName, event: 'INSERT' },
      { table: tableName, event: 'UPDATE' },
      { table: tableName, event: 'DELETE' }
    );
  }

  return filters;
};

/**
 * Constrói a string de filtro baseada na configuração SYNC_CONFIG
 * 
 * @param tableName Nome da tabela
 * @param syncConfig Configuração de sincronização
 * @param userId ID do usuário
 * @param empresaId ID da empresa (opcional)
 * @returns String de filtro no formato "campo=eq.valor"
 */
const buildFilterForConfig = (
  tableName: string,
  syncConfig: any,
  userId: string,
  empresaId: string | null
): string | null => {
  let filterParts: string[] = [];

  // Adiciona filtro por usuário se configurado
  if (syncConfig.filterByUser && syncConfig.userField && userId) {
    //RealtimeLogger.Setup.info(`Aplicando filtro por usuário na tabela ${tableName}: ${syncConfig.userField}=eq.${userId}`);
    filterParts.push(`${syncConfig.userField}=eq.${userId}`);
  }

  // Adiciona filtro por empresa se configurado e disponível
  if (syncConfig.filterByCompany && syncConfig.companyField) {
    const shouldFilterByCompany = APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY || syncConfig.forceCompanyFilter;

    if (shouldFilterByCompany && empresaId) {
      //RealtimeLogger.Setup.info(`Aplicando filtro por empresa na tabela ${tableName}: ${syncConfig.companyField}=eq.${empresaId}`);
      filterParts.push(`${syncConfig.companyField}=eq.${empresaId}`);
    } else if (!shouldFilterByCompany) {
      RealtimeLogger.Setup.info(`Ignorando filtro por empresa para ${tableName} pois o app não requer empresa`);
    } else if (!empresaId) {
      RealtimeLogger.Setup.warn(`Não foi possível aplicar filtro por empresa para ${tableName} pois empresaId não está disponível`);
    }
  }

  // Se não temos filtros, retorna null
  if (filterParts.length === 0) {
    return null;
  }

  // Se temos múltiplos filtros, infelizmente o Supabase Realtime não suporta AND
  // então vamos usar apenas o primeiro filtro (prioridade: empresa > usuário)
  return filterParts[0];
};

/**
 * Obtém a configuração de sincronização para uma tabela específica
 * 
 * @param tableName Nome da tabela
 * @returns Configuração da tabela ou undefined se não existir
 */
const getTableSyncConfig = (tableName: string): any | undefined => {
  // Procura a tabela nas configurações constantes
  for (const tableKey in APP_CONSTANTS.DATABASE.TABLES) {
    // Garante que a propriedade existe no objeto
    if (Object.prototype.hasOwnProperty.call(APP_CONSTANTS.DATABASE.TABLES, tableKey)) {
      const table = APP_CONSTANTS.DATABASE.TABLES[tableKey as keyof typeof APP_CONSTANTS.DATABASE.TABLES];

      if (table.NAME === tableName && table.SYNC_CONFIG) {
        return table.SYNC_CONFIG;
      }
    }
  }

  return undefined;
};

/**
 * Configura uma subscrição para uma tabela com filtros específicos
 * 
 * @param tableName Nome da tabela
 * @param event Tipo de evento (INSERT, UPDATE, DELETE, *)
 * @param filter Filtro opcional para limitar os resultados
 */
const setupTableSubscription = (
  tableName: string,
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
  filter?: string,
  generation: number = subscriptionGeneration
): Promise<boolean> => {
  // O callback fica vinculado à geração que criou o canal. Depois de logout,
  // troca de empresa ou reconexão, eventos antigos devem ser descartados
  // mesmo que o Supabase ainda entregue um callback já enfileirado.
  const channelName = `${tableName}_${event.toLowerCase()}_${Date.now()}`;

  return new Promise(resolve => {
    let settled = false;
    let currentlyReady = false;
    const settle = (isReady: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutHandle);
      resolve(isReady);
    };
    const timeoutHandle = setTimeout(() => {
      registerSubscriptionStatus(
        'TIMED_OUT',
        tableName,
        event,
        `Prazo de ${REALTIME_SUBSCRIPTION_READY_TIMEOUT_MS}ms excedido`
      );
      settle(false);
    }, REALTIME_SUBSCRIPTION_READY_TIMEOUT_MS);

    try {
      //RealtimeLogger.Setup.info(`Configurando subscrição para ${tableName}, evento ${event}${filter ? `, filtro ${filter}` : ''}`);

      const config: any = {
        event: event,
        schema: 'public',
        table: tableName,
      };

      if (filter && event !== 'DELETE') {
        config.filter = filter;
      }

      const channel = supabase
        .channel(channelName)
        .on('postgres_changes', config, async (payload) => {
          RealtimeLogger.Events.info(`Recebido evento ${event} em ${tableName}:`, payload);

          try {
            if ((event === '*' || event === 'INSERT') && payload.eventType === 'INSERT' && payload.new) {
              await processInsertEvent(tableName, payload.new, generation);
            } else if ((event === '*' || event === 'UPDATE') && payload.eventType === 'UPDATE' && payload.new) {
              await processUpdateEvent(tableName, payload.new, generation);
            } else if ((event === '*' || event === 'DELETE') && payload.eventType === 'DELETE' && payload.old) {
              await processDeleteEvent(tableName, payload.old, generation);
            }
          } catch (err) {
            RealtimeLogger.Error.error(`Erro ao processar evento ${event} de ${tableName}:`, err);
          }
        })
        .subscribe((status, err) => {
          const errorDetail = err ? serializeRealtimeError(err) : undefined;
          registerSubscriptionStatus(status, tableName, event, errorDetail);
          currentlyReady = status === 'SUBSCRIBED';

          if (status === 'SUBSCRIBED') {
            settle(true);
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            settle(false);
          }

          if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && errorDetail) {
            RealtimeLogger.Setup.warn(
              `Falha no canal realtime ${tableName}:${event} com status ${status}: ${errorDetail}`
            );
          }
        });

      const subscriptionId = `${tableName}_${event}_${Date.now()}`;
      subscriptions.push({
        id: subscriptionId,
        table: tableName,
        cancelReady: () => settle(false),
        isReady: () => currentlyReady,
        unsubscribe: () => supabase.removeChannel(channel),
      });
    } catch (error) {
      const setupSubscriptionErrorMessage = `Erro ao configurar subscrição ${event} para ${tableName}`;
      RealtimeLogger.Error.warn(`${setupSubscriptionErrorMessage}:`, error);
      captureSentryExceptionWithScope(error, {
        context: {
          ...getRealtimeMonitoringContext(),
          tableName,
          event,
          loggerMessage: setupSubscriptionErrorMessage,
        },
        tags: {
          flow: 'realtime',
          stage: 'channel_setup',
          table: tableName,
          event,
        },
        fingerprint: ['realtime', 'channel_setup_exception', tableName, event],
        dedupeKey: `realtime:channel_setup_exception:${tableName}:${event}`,
        dedupeWindowMs: 30000,
      });
      settle(false);
    }
  });
};

/**
 * Processa um evento de inserção para qualquer tabela
 * 
 * @param tableName Nome da tabela
 * @param data Dados inseridos
 */
const sanitizeRealtimeTablePayload = (tableName: string, data: any, event: 'INSERT' | 'UPDATE' | 'DELETE'): any => {
  const sanitized = sanitizeLocalTablePayload(tableName, data);

  if (sanitized.droppedKeys.length > 0) {
    RealtimeLogger.Events.debug(
      `Evento ${event} em ${tableName} recebeu coluna(s) fora do schema local e elas foram ignoradas: ${sanitized.droppedKeys.join(', ')}`
    );
  }

  return sanitized.data;
};

const isRealtimeDuplicateKeyError = (error: unknown): boolean => {
  const candidate = error as { name?: unknown; message?: unknown } | null | undefined;
  const name = String(candidate?.name ?? '');
  const message = String(candidate?.message ?? error ?? '');
  return name === 'ConstraintError' ||
    /Key already exists/i.test(message) ||
    /UNIQUE constraint failed/i.test(message);
};

type RealtimePostApplyEffect = (() => Promise<void>) | undefined;

const markRemoteChecklistSnapshotPending = (
  tableName: string,
  data: any,
  localSnapshotId?: unknown,
): void => {
  if (tableName !== APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME) return;
  const checklistId = typeof data?.id === 'string' ? data.id : '';
  const snapshotId = typeof data?.snapshot_id === 'string' ? data.snapshot_id : '';
  if (!checklistId || !snapshotId || localSnapshotId === snapshotId) return;

  markChecklistSnapshotPending(checklistId, snapshotId);
  triggerSyncSoon('realtime:snapshot', 250);
};

const processInsertEventCore = async (
  tableName: string,
  rawData: any,
  generation: number
): Promise<RealtimePostApplyEffect> => {
  const data = sanitizeRealtimeTablePayload(tableName, rawData, 'INSERT');

  try {
    const idShort = data.id?.substring(0, 8) || 'desconhecido';
    RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Payload recebido para ${tableName}: ${JSON.stringify(data)}`);
    if (tableName === APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME) {
      RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - next_billing_date recebido: ${JSON.stringify(data.next_billing_date)}`);
    }
    RealtimeLogger.Events.info(`processInsertEvent - Processando inserção em ${tableName} para item: ${idShort}...`);

    // Verifica se já existe no banco local
    if (!isRealtimeGenerationCurrent(generation)) return;
    const existingItem = await databaseService.readItem<any>(tableName, data.id);
    if (!isRealtimeGenerationCurrent(generation)) return;

    if (existingItem) {
      // Se já existe, pode ter sido criado localmente e depois sincronizado
      // Nesse caso, vamos verificar os timestamps
      const localTimestamp = getItemTimestamp(existingItem);
      const remoteTimestamp = getItemTimestamp(data);

      // Log de comparação de timestamps
      RealtimeLogger.Events.info(`processInsertEvent - Comparando timestamps para ${tableName}/${idShort}... - Local: ${new Date(localTimestamp).toISOString()}, Remoto: ${new Date(remoteTimestamp).toISOString()}`);

      if (localTimestamp >= remoteTimestamp) {
        const diffMs = Math.abs(localTimestamp - remoteTimestamp);
        const isChecklist = tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
        const remoteHasSequencialNumero = data?.sequencial_numero !== null && data?.sequencial_numero !== undefined;
        const remoteHasSequencialCodigo = data?.sequencial_codigo !== null && data?.sequencial_codigo !== undefined && data?.sequencial_codigo !== '';
        const localMissingSequencialNumero = existingItem?.sequencial_numero === null || existingItem?.sequencial_numero === undefined;
        const localMissingSequencialCodigo = existingItem?.sequencial_codigo === null || existingItem?.sequencial_codigo === undefined || existingItem?.sequencial_codigo === '';

        // Em checklists, se o servidor trouxe numeração e a diferença for pequena,
        // permitimos mesclar a numeração mesmo com timestamp local mais novo.
        if (
          isChecklist &&
          diffMs <= 10000 &&
          (remoteHasSequencialNumero || remoteHasSequencialCodigo) &&
          (localMissingSequencialNumero || localMissingSequencialCodigo)
        ) {
          const sequencialUpdates: Record<string, any> = {};
          if (remoteHasSequencialNumero && localMissingSequencialNumero) {
            sequencialUpdates.sequencial_numero = data.sequencial_numero;
          }
          if (remoteHasSequencialCodigo && localMissingSequencialCodigo) {
            sequencialUpdates.sequencial_codigo = data.sequencial_codigo;
          }

          if (Object.keys(sequencialUpdates).length > 0) {
            RealtimeLogger.Events.info(
              `processInsertEvent - Mesclando numeração remota em ${tableName}/${idShort} (diffMs=${diffMs})`
            );
            if (!isRealtimeGenerationCurrent(generation)) return;
            const sequenceApplied = await databaseService.updateItem(tableName, data.id, sequencialUpdates, {
              skipPendingOperation: true,
              source: 'remote',
            });
            if (!isRealtimeGenerationCurrent(generation)) return;
            if (!sequenceApplied) {
              RealtimeLogger.Events.warn(
                `processInsertEvent - ACK de numeração não aplicado em ${tableName}/${idShort}: registro local ausente`
              );
              return;
            }
            realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
            return;
          }
        }

        // Dados locais são iguais ou mais recentes, não sobrescrever
        RealtimeLogger.Events.warn(`processInsertEvent - Ignorando inserção para item ${idShort}... em ${tableName}: dados locais iguais ou mais recentes (local: ${new Date(localTimestamp).toISOString()}, remoto: ${new Date(remoteTimestamp).toISOString()})`);
        return;
      }

      // Se já existe, atualiza
      if (!isRealtimeGenerationCurrent(generation)) return;
      const applied = await databaseService.updateItemIfUnchanged(
        tableName,
        data.id,
        data,
        existingItem,
        true
      );
      if (!isRealtimeGenerationCurrent(generation)) return;
      if (!applied) {
        RealtimeLogger.Events.warn(
          `processInsertEvent - Aplicação remota cancelada em ${tableName}/${idShort}: o registro mudou localmente durante a decisão CAS`
        );
        return;
      }
      RealtimeLogger.Events.info(`processInsertEvent - Item ${idShort}... já existia em ${tableName}, atualizado com dados do servidor (remoto mais recente)`);
      markRemoteChecklistSnapshotPending(tableName, data, existingItem?.snapshot_id);
      realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
    } else {
      // Se não existe, cria
      if (!isRealtimeGenerationCurrent(generation)) return;
      try {
        await databaseService.createItem(tableName, data, {
          skipPendingOperation: true,
          source: 'remote',
        });
        if (!isRealtimeGenerationCurrent(generation)) return;
        RealtimeLogger.Events.info(`processInsertEvent - Novo item ${idShort}... inserido em ${tableName} via Realtime`);
        markRemoteChecklistSnapshotPending(tableName, data);
        realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
      } catch (creationError) {
        if (!isRealtimeDuplicateKeyError(creationError)) {
          throw creationError;
        }

        RealtimeLogger.Events.warn(
          `processInsertEvent - Item ${idShort}... foi criado localmente durante o INSERT remoto; relendo para reconciliar`
        );
        if (!isRealtimeGenerationCurrent(generation)) return;
        const currentAfterDuplicate = await databaseService.readItem<any>(tableName, data.id);
        if (!isRealtimeGenerationCurrent(generation) || !currentAfterDuplicate) return;

        const localTimestampAfterDuplicate = getItemTimestamp(currentAfterDuplicate);
        const remoteTimestampAfterDuplicate = getItemTimestamp(data);
        const isChecklist = tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
        const remoteHasSequencialNumero = data?.sequencial_numero !== null && data?.sequencial_numero !== undefined;
        const remoteHasSequencialCodigo = data?.sequencial_codigo !== null && data?.sequencial_codigo !== undefined && data?.sequencial_codigo !== '';
        const localMissingSequencialNumero = currentAfterDuplicate?.sequencial_numero === null || currentAfterDuplicate?.sequencial_numero === undefined;
        const localMissingSequencialCodigo = currentAfterDuplicate?.sequencial_codigo === null || currentAfterDuplicate?.sequencial_codigo === undefined || currentAfterDuplicate?.sequencial_codigo === '';
        const sequencialUpdates: Record<string, unknown> = {};

        if (isChecklist) {
          if (remoteHasSequencialNumero && localMissingSequencialNumero) {
            sequencialUpdates.sequencial_numero = data.sequencial_numero;
          }
          if (remoteHasSequencialCodigo && localMissingSequencialCodigo) {
            sequencialUpdates.sequencial_codigo = data.sequencial_codigo;
          }
        }

        if (Object.keys(sequencialUpdates).length > 0) {
          if (!isRealtimeGenerationCurrent(generation)) return;
          const sequenceApplied = await databaseService.updateItem(
            tableName,
            data.id,
            sequencialUpdates,
            { skipPendingOperation: true, source: 'remote' }
          );
          if (!isRealtimeGenerationCurrent(generation)) return;
          if (!sequenceApplied) {
            RealtimeLogger.Events.warn(
              `processInsertEvent - ACK de numeração não aplicado em ${tableName}/${idShort} após duplicidade`
            );
            return;
          }
          realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
          return;
        }

        if (localTimestampAfterDuplicate >= remoteTimestampAfterDuplicate) {
          RealtimeLogger.Events.warn(
            `processInsertEvent - Duplicidade reconciliada sem sobrescrita em ${tableName}/${idShort}: versão local é igual ou mais recente`
          );
          return;
        }

        if (!isRealtimeGenerationCurrent(generation)) return;
        const appliedAfterDuplicate = await databaseService.updateItemIfUnchanged(
          tableName,
          data.id,
          data,
          currentAfterDuplicate,
          true
        );
        if (!isRealtimeGenerationCurrent(generation)) return;
        if (!appliedAfterDuplicate) {
          RealtimeLogger.Events.warn(
            `processInsertEvent - CAS após duplicidade cancelado em ${tableName}/${idShort}: o registro mudou localmente`
          );
          return;
        }
        realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
      }
    }

    const postApplyEffect = async (): Promise<void> => {
      // Tratamento especial para imagens de marcação de checklist (background)
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME) {
        await saveOrUpdateMarcacaoBkgImage(data);
        if (!isRealtimeGenerationCurrent(generation)) return;
      }

      // Processamento especial para o usuário logado
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.USERS.NAME && data.id === globalState.user.user.get()?.id) {
        RealtimeLogger.Events.info(`processInsertEvent - Atualização afeta o usuário logado, atualizando perfil no estado global`);

        // Merge seguro (sem undefined)
        const safeProfile = Object.fromEntries(
          Object.entries(data).filter(([_, v]) => v !== undefined)
        ) as Partial<UserProfile>;

        userActions.setProfile({
          ...globalState.user.profile.get()!,
          ...safeProfile
        });
      }

      // Processamento especial para empresa do usuário logado
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME) {
        // Obter o ID do usuário e da empresa logada
        const userId = globalState.user.user.get()?.id;
        const empresaIdDoUsuario = globalState.user.profile.get()?.empresa_id;

        // Verificar se é a empresa do usuário atual
        if (userId && empresaIdDoUsuario === data.id) {
          RealtimeLogger.Events.info(`processInsertEvent - Atualização afeta a empresa do usuário logado, atualizando estado global`);

          // Assegurar que temos as propriedades mínimas obrigatórias
          if (data.id && data.nome) {
            // Merge seguro (sem undefined)
            const safeCompany = Object.fromEntries(
              Object.entries(data).filter(([_, v]) => v !== undefined)
            ) as unknown as CompanyProfile;

            // Atualizar o estado global da empresa
            companyActions.setCompanyData(safeCompany);
          } else {
            RealtimeLogger.Events.warn(`processInsertEvent - Dados da empresa insuficientes para atualizar estado global`);
          }
        }
      }

      // Processamento especial para assinatura da empresa do usuário
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME) {
        // Obter o ID do usuário logado
        const userId = globalState.user.user.get()?.id;
        if (userId) {
          // Buscar dados do usuário no banco local
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Buscando informações do usuário ${userId.substring(0, 8)}... no banco local`);
          if (!isRealtimeGenerationCurrent(generation)) return;
          const localUser = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
          if (!isRealtimeGenerationCurrent(generation)) return;
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Usuário local: ${JSON.stringify(localUser || {})}`);
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Empresa da assinatura: ${data.empresa_id}`);
          if (localUser && localUser.empresa_id === data.empresa_id && isRealtimeGenerationCurrent(generation)) {
            RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Nova assinatura detectada para a empresa do usuário, atualizando estado global`);
            if (!isRealtimeGenerationCurrent(generation)) return;
            const subscriptionStatus = await checkSubscriptionStatus(data.empresa_id);
            RealtimeLogger.Events.info(`[DEBUG-PAUSE] processInsertEvent - Valor retornado por checkSubscriptionStatus: ${JSON.stringify(subscriptionStatus)}`);
            if (!isRealtimeGenerationCurrent(generation)) return;
            userActions.setSubscriptionStatus(subscriptionStatus);
          }
        }
      }
    };
    return postApplyEffect;
  } catch (error) {
    RealtimeLogger.Error.error(`[DEBUG-PAUSE] processInsertEvent - Erro ao processar inserção em ${tableName} para item ${data?.id?.substring(0, 8) || 'desconhecido'}:`, error);
    throw error;
  }
};

/**
 * Processa um evento de atualização para qualquer tabela
 * 
 * @param tableName Nome da tabela
 * @param data Dados atualizados
 */
const processUpdateEventCore = async (
  tableName: string,
  rawData: any,
  generation: number
): Promise<RealtimePostApplyEffect> => {
  const data = sanitizeRealtimeTablePayload(tableName, rawData, 'UPDATE');

  try {
    const idShort = data.id?.substring(0, 8) || 'desconhecido';
    RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Payload recebido para ${tableName}: ${JSON.stringify(data)}`);
    if (tableName === APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME) {
      RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - next_billing_date recebido: ${JSON.stringify(data.next_billing_date)}`);
    }
    RealtimeLogger.Events.info(`processUpdateEvent - Processando atualização em ${tableName} para item: ${idShort}...`);

    // Verificar se a atualização é mais recente que os dados locais
    if (!isRealtimeGenerationCurrent(generation)) return;
    const existingItem = await databaseService.readItem<any>(tableName, data.id);
    if (!isRealtimeGenerationCurrent(generation)) return;

    if (!existingItem) {
      // Se não existe localmente, tentar inserir; se já tiver chegado via outro canal, fazer fallback para update
      try {
        if (!isRealtimeGenerationCurrent(generation)) return;
        await databaseService.createItem(tableName, data, {
          skipPendingOperation: true,
          source: 'remote',
        });
        if (!isRealtimeGenerationCurrent(generation)) return;
        RealtimeLogger.Events.info(`processUpdateEvent - Item ${idShort}... não encontrado em ${tableName}, criado via evento UPDATE Realtime`);
        markRemoteChecklistSnapshotPending(tableName, data);
        realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
        return;
      } catch (creationError: any) {
        if (isRealtimeDuplicateKeyError(creationError)) {
          RealtimeLogger.Events.warn(`processUpdateEvent - Item ${idShort}... já existia ao tentar criar (chave duplicada); executando update como fallback`);
          if (!isRealtimeGenerationCurrent(generation)) return;
          const currentAfterDuplicate = await databaseService.readItem<any>(tableName, data.id);
          if (!isRealtimeGenerationCurrent(generation) || !currentAfterDuplicate) return;
          const currentTimestamp = getItemTimestamp(currentAfterDuplicate);
          const remoteTimestampAfterDuplicate = getItemTimestamp(data);
          if (currentTimestamp >= remoteTimestampAfterDuplicate
            && !hasNewerTemplateVersionAtSameTimestamp(tableName, currentAfterDuplicate, data)) {
            RealtimeLogger.Events.warn(
              `processUpdateEvent - Fallback de chave duplicada ignorado em ${tableName}/${idShort}: versão local é igual ou mais recente`
            );
            return;
          }
          const tieGuard = hasNewerTemplateVersionAtSameTimestamp(tableName, currentAfterDuplicate, data)
            ? await buildTemplateVersionTieGuard(data.id, generation) : undefined;
          if (tieGuard === null || !isRealtimeGenerationCurrent(generation)) return;
          const args = [tableName, data.id, data, currentAfterDuplicate, true] as const;
          const fallbackApplied = tieGuard
            ? await databaseService.updateItemIfUnchanged(...args, undefined, tieGuard)
            : await databaseService.updateItemIfUnchanged(...args);
          if (!isRealtimeGenerationCurrent(generation)) return;
          if (!fallbackApplied) return;
          realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
          return;
        }
        throw creationError;
      }
      return;
    }

    // Verificar timestamps para garantir que não substituímos dados mais recentes
    const localTimestamp = getItemTimestamp(existingItem);
    const remoteTimestamp = getItemTimestamp(data);

    // Log de comparação de timestamps
    RealtimeLogger.Events.info(`processUpdateEvent - Comparando timestamps para ${tableName}/${idShort}... - Local: ${new Date(localTimestamp).toISOString()}, Remoto: ${new Date(remoteTimestamp).toISOString()}`);

    if (localTimestamp >= remoteTimestamp
      && !hasNewerTemplateVersionAtSameTimestamp(tableName, existingItem, data)) {
      const isChecklistSnapshotEnrichment = (
        tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME
        && !existingItem?.snapshot_id
        && typeof data?.snapshot_id === 'string'
        && data.snapshot_id.trim().length > 0
      );
      if (isChecklistSnapshotEnrichment) {
        const applied = await databaseService.updateItem(
          tableName,
          data.id,
          { snapshot_id: data.snapshot_id },
          { skipPendingOperation: true, source: 'remote' },
        );
        if (!isRealtimeGenerationCurrent(generation) || !applied) return;
        markRemoteChecklistSnapshotPending(tableName, data, existingItem?.snapshot_id);
        RealtimeLogger.Events.info(
          `processUpdateEvent - Snapshot remoto associado a ${tableName}/${idShort} sem rebaixar a versão local`,
        );
        realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);
        return;
      }
      RealtimeLogger.Events.warn(`processUpdateEvent - Conflito detectado para item ${idShort}... em ${tableName}: versão local (${new Date(localTimestamp).toISOString()}) é igual ou mais recente que servidor (${new Date(remoteTimestamp).toISOString()})`);
      // TODO: Aqui poderíamos registrar para sincronização posterior, mantendo a versão local
      return;
    }

    // Atualizar o registro no banco local
    if (!isRealtimeGenerationCurrent(generation)) return;
    const tieGuard = hasNewerTemplateVersionAtSameTimestamp(tableName, existingItem, data)
      ? await buildTemplateVersionTieGuard(data.id, generation) : undefined;
    if (tieGuard === null || !isRealtimeGenerationCurrent(generation)) return;
    const args = [tableName, data.id, data, existingItem, true] as const;
    const applied = tieGuard
      ? await databaseService.updateItemIfUnchanged(...args, undefined, tieGuard)
      : await databaseService.updateItemIfUnchanged(...args);
    if (!isRealtimeGenerationCurrent(generation)) return;
    if (!applied) {
      RealtimeLogger.Events.warn(
        `processUpdateEvent - Aplicação remota cancelada em ${tableName}/${idShort}: o registro mudou localmente durante a decisão CAS`
      );
      return;
    }
    RealtimeLogger.Events.info(`processUpdateEvent - Item ${idShort}... atualizado com sucesso em ${tableName} (servidor mais recente)`);
    markRemoteChecklistSnapshotPending(tableName, data, existingItem?.snapshot_id);
    realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);

    const postApplyEffect = async (): Promise<void> => {
      // Imagem de background de marcação
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME) {
        await saveOrUpdateMarcacaoBkgImage(data);
        if (!isRealtimeGenerationCurrent(generation)) return;
      }

      // Processamento especial para o usuário logado
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.USERS.NAME && data.id === globalState.user.user.get()?.id) {
        RealtimeLogger.Events.info(`processUpdateEvent - Atualização afeta o usuário logado, atualizando perfil no estado global`);

        // Merge seguro (sem undefined)
        const safeUpdate = Object.fromEntries(
          Object.entries(data).filter(([_, v]) => v !== undefined)
        ) as Partial<UserProfile>;

        const updatedProfile: UserProfile = {
          ...globalState.user.profile.get()!,
          ...safeUpdate
        };
        userActions.setProfile(updatedProfile);
      }

      // Processamento especial para empresa do usuário logado
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME) {
        // Obter o ID da empresa do usuário atual
        const empresaIdDoUsuario = globalState.user.profile.get()?.empresa_id;

        // Verificar se a atualização afeta a empresa do usuário
        if (empresaIdDoUsuario === data.id) {
          RealtimeLogger.Events.info(`processUpdateEvent - Atualização afeta a empresa do usuário logado, atualizando estado global`);

          // Merge seguro (sem undefined)
          const safeUpdate = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => v !== undefined)
          ) as unknown as Partial<CompanyProfile>;

          // Obter dados existentes
          const currentCompany = globalState.company.data.get();

          // Só atualizar se já tivermos dados ou se os novos dados tiverem as propriedades mínimas
          if (currentCompany || (data.id && data.nome)) {
            const updatedCompany: CompanyProfile = currentCompany
              ? { ...currentCompany, ...safeUpdate }
              : safeUpdate as unknown as CompanyProfile;

            // Atualizar o estado global
            companyActions.setCompanyData(updatedCompany);
          } else {
            RealtimeLogger.Events.warn(`processUpdateEvent - Dados da empresa insuficientes para atualizar estado global`);
          }
        }
      }

      // Processamento especial para assinatura da empresa do usuário
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME) {
        // Obter o ID do usuário logado
        const userId = globalState.user.user.get()?.id;

        if (userId) {
          // Buscar dados do usuário no banco local
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Buscando informações do usuário ${userId.substring(0, 8)}... no banco local`);
          if (!isRealtimeGenerationCurrent(generation)) return;
          const localUser = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
          if (!isRealtimeGenerationCurrent(generation)) return;
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Dados do usuário local: ${JSON.stringify(localUser || {})}`);
          RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Empresa da assinatura: ${data.empresa_id}`);
          if (localUser && localUser.empresa_id === data.empresa_id && isRealtimeGenerationCurrent(generation)) {
            RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Atualização da assinatura detectada para a empresa do usuário, atualizando estado global`);
            if (!isRealtimeGenerationCurrent(generation)) return;
            const subscriptionStatus = await checkSubscriptionStatus(data.empresa_id);
            RealtimeLogger.Events.info(`[DEBUG-PAUSE] processUpdateEvent - Valor retornado por checkSubscriptionStatus: ${JSON.stringify(subscriptionStatus)}`);
            if (!isRealtimeGenerationCurrent(generation)) return;
            userActions.setSubscriptionStatus(subscriptionStatus);
          }
        }
      }
    };
    return postApplyEffect;
  } catch (error) {
    RealtimeLogger.Error.error(`[DEBUG-PAUSE] processUpdateEvent - Erro ao processar atualização em ${tableName} para item ${data?.id?.substring(0, 8) || 'desconhecido'}:`, error);
  }
};

// INSERT, UPDATE e DELETE chegam por canais distintos; todos passam pela mesma fila
// para que a leitura, a decisão pelo timestamp e a aplicação sejam uma única
// sequência determinística. Essa fila só recebe handlers Realtime; o ciclo de
// sync inicial, full-state e PDF não passa por ela.
const processInsertEvent = async (
  tableName: string,
  rawData: any,
  generation: number = subscriptionGeneration
): Promise<void> => {
  const databaseTask = enqueueRealtimeDatabaseTask(
    () => processInsertEventCore(tableName, rawData, generation),
    `INSERT ${tableName}/${rawData?.id || 'desconhecido'}`
  );
  try {
    await enqueueRealtimePostApplyTask(getRealtimePostApplyKey(tableName, rawData), async () => {
      const postApplyEffect = await databaseTask;
      if (postApplyEffect && isRealtimeGenerationCurrent(generation)) await postApplyEffect();
    });
  } catch (error) {
    RealtimeLogger.Error.error(`processInsertEvent - Erro em efeito pos-aplicacao para ${tableName}:`, error);
  }
};

const processUpdateEvent = async (
  tableName: string,
  rawData: any,
  generation: number = subscriptionGeneration
): Promise<void> => {
  const databaseTask = enqueueRealtimeDatabaseTask(
    () => processUpdateEventCore(tableName, rawData, generation),
    `UPDATE ${tableName}/${rawData?.id || 'desconhecido'}`
  );
  try {
    await enqueueRealtimePostApplyTask(getRealtimePostApplyKey(tableName, rawData), async () => {
      const postApplyEffect = await databaseTask;
      if (postApplyEffect && isRealtimeGenerationCurrent(generation)) await postApplyEffect();
    });
  } catch (error) {
    RealtimeLogger.Error.error(`processUpdateEvent - Erro em efeito pos-aplicacao para ${tableName}:`, error);
  }
};

/**
 * Processa um evento de exclusão para qualquer tabela
 * 
 * @param tableName Nome da tabela
 * @param data Dados do item excluído
 */
const processDeleteEventCore = async (
  tableName: string,
  rawData: any,
  generation: number
): Promise<RealtimePostApplyEffect> => {
  const data = sanitizeRealtimeTablePayload(tableName, rawData, 'DELETE');

  try {
    if (!isRealtimeGenerationCurrent(generation)) return;
    const itemId = data?.id != null ? String(data.id) : '';
    if (!itemId) {
      RealtimeLogger.Events.warn(
        `processDeleteEvent - Evento DELETE em ${tableName} ignorado porque o payload remoto nao trouxe id local. Verifique replica identity/payload da tabela.`
      );
      return;
    }

    const idShort = itemId.substring(0, 8);
    RealtimeLogger.Events.info(`processDeleteEvent - Processando exclusão em ${tableName} para item: ${idShort}...`);

    // Adicionar log detalhado do item sendo excluído
    const itemTimestamp = getItemTimestamp(data);
    RealtimeLogger.Events.info(`processDeleteEvent - Dados do item para exclusão em ${tableName}/${idShort}... - Timestamp remoto: ${itemTimestamp ? new Date(itemTimestamp).toISOString() : 'não disponível'}`);

    // A exclusão pertence à mesma ordem FIFO de INSERT/UPDATE. Estado,
    // arquivos e leituras auxiliares ficam para o efeito posterior.
    if (!isRealtimeGenerationCurrent(generation)) return;
    const deleted = await databaseService.deleteItem(tableName, itemId, {
      skipPendingOperation: true,
      source: 'remote',
    });
    if (!isRealtimeGenerationCurrent(generation)) return;

    const cleanupImageEffect = async (): Promise<void> => {
      if (tableName !== APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME) return;
      if (!isRealtimeGenerationCurrent(generation)) return;
      await deleteMarcacaoBkgImage(data);
      if (!isRealtimeGenerationCurrent(generation)) return;
    };

    if (!deleted) {
      RealtimeLogger.Events.warn(`processDeleteEvent - Item ${idShort}... não estava presente em ${tableName}; mantendo apenas eventual limpeza local`);
      return tableName === APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME
        ? cleanupImageEffect
        : undefined;
    }
    RealtimeLogger.Events.info(`processDeleteEvent - Item ${idShort}... excluído com sucesso de ${tableName} via Realtime`);
    realtimeTrigger.counter.set(realtimeTrigger.counter.get() + 1);

    return async (): Promise<void> => {
      if (!isRealtimeGenerationCurrent(generation)) return;

      // Verificações especiais após a exclusão do banco local
      if (tableName === APP_CONSTANTS.DATABASE.TABLES.USERS.NAME) {
        // Se for o usuário atual, fazer logout
        if (itemId === globalState.user.user.get()?.id) {
          RealtimeLogger.Events.warn(`processDeleteEvent - Usuário atual ${idShort}... foi excluído remotamente, iniciando logout`);

          // Limpar o perfil do estado global
          userActions.setProfile(null);

          // Fazer logout do usuário
          userActions.clearUser();
          // TODO: Implementar chamada para authService.signOut()
        }
      } else if (tableName === APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME) {
        // Se for a empresa do usuário atual, atualizar o estado
        if (!isRealtimeGenerationCurrent(generation)) return;
        const currentUser = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, globalState.user.user.get()?.id || '');
        if (!isRealtimeGenerationCurrent(generation)) return;
        if (currentUser && currentUser.empresa_id === itemId) {
          RealtimeLogger.Events.warn(`processDeleteEvent - Empresa ${idShort}... do usuário atual foi excluída remotamente, ações necessárias`);
          // TODO: Implementar lógica de tratamento aqui se necessário
        }
      } else if (tableName === APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME) {
        // Obter o ID do usuário logado
        const userId = globalState.user.user.get()?.id;

        if (userId) {
          // Buscar dados do usuário no banco local
          RealtimeLogger.Events.info(`processDeleteEvent - Buscando informações do usuário ${userId.substring(0, 8)}... no banco local`);
          if (!isRealtimeGenerationCurrent(generation)) return;
          const localUser = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
          if (!isRealtimeGenerationCurrent(generation)) return;

          RealtimeLogger.Events.info(`processDeleteEvent - Dados do usuário local: ${JSON.stringify(localUser || {})}`);
          RealtimeLogger.Events.info(`processDeleteEvent - Empresa da assinatura: ${data.empresa_id}`);

          // Verificar se a empresa da assinatura é a mesma do usuário
          if (localUser && localUser.empresa_id === data.empresa_id) {
            RealtimeLogger.Events.info(`processDeleteEvent - Assinatura ${idShort}... da empresa do usuário foi excluída, atualizando estado global`);
            if (!isRealtimeGenerationCurrent(generation)) return;
            const subscriptionStatus = await checkSubscriptionStatus(data.empresa_id);
            if (!isRealtimeGenerationCurrent(generation)) return;
            userActions.setSubscriptionStatus(subscriptionStatus);
          }
        }
      }

      await cleanupImageEffect();
    };
  } catch (error) {
    RealtimeLogger.Error.error(`processDeleteEvent - Erro ao processar exclusão em ${tableName} para item ${data?.id?.substring(0, 8) || 'desconhecido'}:`, error);
  }
};

const processDeleteEvent = async (
  tableName: string,
  rawData: any,
  generation: number = subscriptionGeneration
): Promise<void> => {
  const databaseTask = enqueueRealtimeDatabaseTask(
    () => processDeleteEventCore(tableName, rawData, generation),
    `DELETE ${tableName}/${rawData?.id || 'desconhecido'}`
  );
  try {
    await enqueueRealtimePostApplyTask(getRealtimePostApplyKey(tableName, rawData), async () => {
      const postApplyEffect = await databaseTask;
      if (postApplyEffect && isRealtimeGenerationCurrent(generation)) await postApplyEffect();
    });
  } catch (error) {
    RealtimeLogger.Error.error(`processDeleteEvent - Erro em efeito pos-aplicacao para ${tableName}:`, error);
  }
};

/**
 * Extrai timestamp de um objeto, tentando diferentes campos comuns
 * 
 * @param obj Objeto para extrair o timestamp
 * @returns Timestamp em milissegundos, ou 0 se não encontrado
 */
const getItemTimestamp = (obj: any): number => {
  if (obj.updated_at) return new Date(obj.updated_at).getTime();
  if (obj.updatedAt) return new Date(obj.updatedAt).getTime();
  if (obj.timestamp) return new Date(obj.timestamp).getTime();
  return 0;
};

// Uma transação pode incrementar o modelo várias vezes com o mesmo updated_at.
const hasNewerTemplateVersionAtSameTimestamp = (
  tableName: string,
  local: any,
  remote: any,
): boolean => {
  if (tableName !== APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME) return false;
  const timestamp = getItemTimestamp(local);
  if (!Number.isFinite(timestamp) || timestamp <= 0 || timestamp !== getItemTimestamp(remote)) return false;
  const localVersion = Number(local.versao);
  const remoteVersion = Number(remote.versao);
  return Number.isInteger(localVersion) && localVersion > 0
    && Number.isInteger(remoteVersion) && remoteVersion > localVersion;
};

const buildTemplateVersionTieGuard = async (
  templateId: string,
  generation: number,
): Promise<HistoricalInsertGuard | null> => {
  const table = APP_CONSTANTS.DATABASE.TABLES.PENDING_OPERATIONS.NAME;
  const pending = await databaseService.listItems<Record<string, unknown>>(table);
  if (pending.some(row => row.collection === APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME
    && row.item_id === templateId)) return null;
  // A fila é reconferida dentro do CAS para não substituir uma edição que chegou após a leitura.
  return {
    canApply: () => isRealtimeGenerationCurrent(generation),
    rows: [],
    tables: [{ table, expected: pending }],
  };
};

/**
 * Limpa todas as subscrições de realtime
 */
export const cleanupRealtimeSubscriptions = () => {
  subscriptionGeneration += 1;
  RealtimeLogger.Cleanup.info(`Limpando subscrições realtime: ${subscriptions.length} canais`);

  // Remove cada subscrição
  subscriptions.forEach(sub => {
    try {
      sub.cancelReady?.();
      sub.unsubscribe();
      RealtimeLogger.Cleanup.info(`Removida subscrição: ${sub.id} (${sub.table})`);
    } catch (error) {
      RealtimeLogger.Error.error(`Erro ao remover subscrição ${sub.id}:`, error);
    }
  });

  // Limpa o array
  subscriptions.length = 0;

  // Atualiza o estado global
  syncActions.clearRealtimeSubscriptions();

  RealtimeLogger.Cleanup.info('Todas as subscrições realtime foram limpas');
};

/**
 * Verifica se as subscrições estão ativas
 * 
 * @returns Booleano indicando se há subscrições ativas
 */
export const hasActiveSubscriptions = (): boolean => {
  return subscriptions.length > 0;
};

/**
 * Reconecta todas as subscrições após uma falha de conexão
 */
export const reconnectSubscriptions = async (): Promise<boolean> => {
  RealtimeLogger.Setup.info('Tentando reconectar subscrições após falha de rede');

  // Limpa as subscrições existentes
  cleanupRealtimeSubscriptions();

  // Obtém o ID do usuário atual
  const userId = globalState.user.user.get()?.id;
  if (!userId) {
    RealtimeLogger.Setup.warn('Não é possível reconectar: usuário não autenticado');
    return false;
  }

  // Configura novas subscrições
  return await setupRealtimeSubscriptions(userId);
};

/**
 * Salva ou atualiza uma imagem de background de marcação localmente (web ou mobile).
 * Segue o padrão: rowId.jpg na pasta checklist-marcacao-bkg/<empresaId>/ para mobile
 *               e registro em CHECKLIST_MARCACAO_BKG_IMAGES para web.
 */
const saveOrUpdateMarcacaoBkgImage = async (marcacao: any): Promise<void> => {
  try {
    const { id, foto_url, empresa_id, template_id, campo_id } = marcacao;

    if (!foto_url || typeof foto_url !== 'string') {
      RealtimeLogger.Events.warn(`saveOrUpdateMarcacaoBkgImage - Registro ${id} não possui foto_url válida`);
      return;
    }

    // Se já for um URI local, não há nada a fazer
    if (!foto_url.startsWith('http')) {
      RealtimeLogger.Events.info(`saveOrUpdateMarcacaoBkgImage - Imagem ${id} já é local (${foto_url}), ignorando download`);
      return;
    }

    // Web preserva o fluxo já validado de Blob para base64.
    let base64: string | null = null;
    if (Platform.OS === 'web') {
      try {
        const response = await fetch(foto_url);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const blob = await response.blob();
        const reader = new FileReader();
        base64 = await new Promise<string>((resolve, reject) => {
          reader.onerror = () => reject(reader.error);
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(blob);
        });
      } catch (err) {
        RealtimeLogger.Error.error(`saveOrUpdateMarcacaoBkgImage - Falha ao baixar/converter imagem ${id}:`, err);
        return;
      }
    }

    if (Platform.OS === 'web') {
      // Persistir/atualizar no IndexedDB
      const localImageData = {
        id,
        base64,
        empresa_id,
        template_id,
        campo_id,
        foto_url: id, // no indexedDB a foto_url referencia o próprio id
        created_at: Date.now(),
        updated_at: Date.now(),
      };

      const existing = await databaseService.readItem<any>(
        APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
        id
      );
      if (existing) {
        await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
          id,
          localImageData,
          { skipPendingOperation: true, source: 'local' }
        );
      } else {
        await databaseService.createItem(
          APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
          localImageData,
          { skipPendingOperation: true, source: 'local' }
        );
      }
      RealtimeLogger.Events.info(`saveOrUpdateMarcacaoBkgImage - Imagem ${id} salva/atualizada no IndexedDB`);
    } else {
      // Mobile: salvar arquivo físico
      try {
        const fileName = `${id}.jpg`;
        const dirPath = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresa_id}/`;
        const fileUri = `${dirPath}${fileName}`;

        // Criar diretório se necessário
        const dirInfo = await FileSystem.getInfoAsync(dirPath);
        if (!dirInfo.exists) {
          await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
        }

        await downloadRemoteImageToFile(foto_url, fileUri, FileSystem);
        RealtimeLogger.Events.info(`saveOrUpdateMarcacaoBkgImage - Imagem ${id} salva/atualizada em ${fileUri}`);
      } catch (err) {
        RealtimeLogger.Error.error(`saveOrUpdateMarcacaoBkgImage - Erro ao salvar imagem ${id} no mobile:`, err);
      }
    }
  } catch (err) {
    RealtimeLogger.Error.error('saveOrUpdateMarcacaoBkgImage - Erro inesperado:', err);
  }
};

/**
 * Remove a imagem de background local (web ou mobile) quando o registro é excluído.
 */
const deleteMarcacaoBkgImage = async (marcacao: any): Promise<void> => {
  try {
    const { id, empresa_id } = marcacao;

    if (Platform.OS === 'web') {
      try {
        await databaseService.deleteItem(
          APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
          id,
          { skipPendingOperation: true, source: 'local' }
        );
        RealtimeLogger.Events.info(`deleteMarcacaoBkgImage - Imagem ${id} removida do IndexedDB`);
      } catch (err) {
        RealtimeLogger.Error.error(`deleteMarcacaoBkgImage - Falha ao remover imagem ${id} do IndexedDB:`, err);
      }
    } else {
      try {
        const possibleExts = ['jpg', 'png'];
        for (const ext of possibleExts) {
          const uri = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresa_id}/${id}.${ext}`;
          const info = await FileSystem.getInfoAsync(uri);
          if (info.exists) {
            await FileSystem.deleteAsync(uri, { idempotent: true });
            RealtimeLogger.Events.info(`deleteMarcacaoBkgImage - Arquivo ${uri} removido`);
          }
        }
      } catch (err) {
        RealtimeLogger.Error.error(`deleteMarcacaoBkgImage - Falha ao remover arquivos da imagem ${id}:`, err);
      }
    }
  } catch (err) {
    RealtimeLogger.Error.error('deleteMarcacaoBkgImage - Erro inesperado:', err);
  }
};

export const __realtimeTestHooks = {
  processInsertEvent,
  processUpdateEvent,
  processDeleteEvent,
  enqueuePostApplyEffect: enqueueRealtimePostApplyTask,
  sanitizeRealtimeTablePayload,
  setupTableSubscription,
  areAllSubscriptionsReady,
  isRealtimeSetupIdentityCurrent,
};

export default {
  setupRealtimeSubscriptions,
  cleanupRealtimeSubscriptions,
  hasActiveSubscriptions,
  reconnectSubscriptions
}; 
